import { Breadcrumbs, Container, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Translation, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";

// helper
import { checkPermission } from "../../helper/PermissionHelper";
import { pageName } from "../../reducers/commonSlice";
import { user } from "../../reducers/userSlice";

// components
import ActionFooter from "../../components/ActionFooter";
import FullScreenLoading from "../../components/FullScreenLoading";
import BaseInfo from "../../components/member/BaseInfo";

// api
import { CategoryApi } from "../../api/CategoryApi";
import { MemberApi } from "../../api/MemberApi";

// validation schema
const validationSchema = Yup.object().shape({
  firstName: Yup.string().required(
    <Translation>{t => t("error.first_name_is_required")}</Translation>,
  ),
  lastName: Yup.string().required(
    <Translation>{t => t("error.last_name_is_required")}</Translation>,
  ),
  title: Yup.string().required(<Translation>{t => t("error.job_title_is_required")}</Translation>),
  phoneNumber: Yup.string()
    .required(<Translation>{t => t("error.phone_number_is_required")}</Translation>)
    .matches(/^[0-9]+$/, <Translation>{t => t("error.phone_number_is_invalid")}</Translation>),
});

export default function Edit({ onClose, onUpdated }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [catetoryPage, setCatetoryPage] = useState(1);
  const [catetoryPageSize, setCatetoryPageSize] = useState(-1);
  const [catetoryFilter, setCatetoryFilter] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const { id } = useParams();
  const formik = useFormik({
    initialValues: {
      customerNum: "",
      photo: null,
      firstName: null,
      lastName: null,
      title: null,
      categoryId: 1,
      areaCode: "+852",
      phoneNumber: null,
    },
    validateOnMount: true,
    validationSchema: validationSchema,
  });

  const [fullScreenLoading, setFullScreenLoading] = useState(false);
  let memberId = id;
  const [haveDeletePermission, setHaveDeletePermission] = useState(false);
  const currentUser = useSelector(user);
  const currentPage = useSelector(pageName);

  useEffect(() => {
    if (!memberId) {
      navigate(-1);
    }
    fetchData();
    fetchCategoryList();
  }, []);

  useEffect(() => {
    const deletePermission = checkPermission(currentUser, currentPage, "delete");
    setHaveDeletePermission(deletePermission);
  }, [currentUser, currentPage]);

  const fetchCategoryList = async () => {
    try {
      setFullScreenLoading(true);
      const response = await CategoryApi.list(catetoryPage, catetoryPageSize, catetoryFilter);

      if (response.data) {
        setCategoryList(response.data);
      }

      setFullScreenLoading(false);
    } catch (error) {
      console.log(error);
    } finally {
      setFullScreenLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setFullScreenLoading(true);
      const response = await MemberApi.details(memberId);
      formik.setValues(response);
      setFullScreenLoading(false);
    } catch (error) {
      console.log(error);
    } finally {
      setFullScreenLoading(false);
    }
  };

  const update = async () => {
    try {
      formik.setTouched({
        firstName: true,
        lastName: true,
        title: true,
        phoneNumber: true,
      });

      formik.validateForm();

      if (!formik.isValid) {
        return;
      }

      const member = {
        ...formik.values,
      };

      setFullScreenLoading(true);

      const success = await MemberApi.update(memberId, member);

      if (success) {
        toast.success(t("layout.updated_successfully"));
        fetchData();
      }

      setFullScreenLoading(false);
    } catch (error) {
      console.log(error);
    } finally {
      setFullScreenLoading(false);
    }
  };

  const onCancel = () => {
    navigate(-1);
  };

  const onDelete = async () => {
    try {
      setFullScreenLoading(true);
      const success = await MemberApi.deleteData(id);
      if (success) {
        toast.success(t("layout.deleted_successfully"));
        navigate("/members");
      }
      setFullScreenLoading(false);
    } catch (error) {
      console.log(error);
    } finally {
      setFullScreenLoading(false);
    }
  };

  return (
    <Container maxWidth="false">
      <FullScreenLoading open={fullScreenLoading} />
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to={"/members"}>{t("menu.members")}</Link>
        <Typography color="text.primary">{t("layout.edit")}</Typography>
      </Breadcrumbs>
      <BaseInfo
        formik={formik}
        onDelete={onDelete}
        haveDeletePermission={haveDeletePermission}
        categoryList={categoryList}
      />
      <ActionFooter onSubmit={update} onCancel={onCancel} />
    </Container>
  );
}
